import { useState, useMemo } from 'react';
import { Folder, ChevronsDown, ChevronsUp } from 'lucide-react';
import * as Icons from 'lucide-react';
import { catalog_show_path } from '@/lib/routes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AppCard } from '@/pages/WorkflowsCatalog/AppCard';
import { collectAllFolderPaths, countWorkflowsInFolder } from './utils';
import type { Workflow } from '@/pages/WorkflowsCatalog/types';

export interface FolderNode {
  name: string;
  path: string;
  workflows: Workflow[];
  subfolders: FolderNode[];
  level: number;
  isFolder: boolean;
}

// Custom hook for managing folder expansion state
export function useFolderExpansion(
  folderStructure: FolderNode[],
  filteredFolders: FolderNode[],
  searchQuery: string
) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // Auto-expand folders with search results
  useMemo(() => {
    if (searchQuery.trim()) {
      const pathsToExpand = collectAllFolderPaths(filteredFolders);

      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        pathsToExpand.forEach((path) => newSet.add(path));
        return newSet;
      });
    }
  }, [searchQuery, filteredFolders]);

  const handleToggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    if (allExpanded) {
      // Collapse all
      setExpandedFolders(new Set());
      setAllExpanded(false);
    } else {
      // Expand all
      const allPaths = collectAllFolderPaths(folderStructure);
      setExpandedFolders(allPaths);
      setAllExpanded(true);
    }
  };

  const handleExpandAll = (folder: FolderNode) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      // Add the folder itself
      newSet.add(folder.path);
      // Add all subfolders recursively
      const collectPaths = (f: FolderNode) => {
        f.subfolders.forEach((subfolder) => {
          newSet.add(subfolder.path);
          collectPaths(subfolder);
        });
      };
      collectPaths(folder);
      return newSet;
    });
  };

  const handleCollapseAll = (folder: FolderNode) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      // Remove the folder itself
      newSet.delete(folder.path);
      // Remove all subfolders recursively
      const collectPaths = (f: FolderNode) => {
        f.subfolders.forEach((subfolder) => {
          newSet.delete(subfolder.path);
          collectPaths(subfolder);
        });
      };
      collectPaths(folder);
      return newSet;
    });
  };

  return {
    expandedFolders,
    allExpanded,
    handleToggleFolder,
    handleToggleAll,
    handleExpandAll,
    handleCollapseAll,
  };
}

interface WorkflowFolderProps {
  folder: FolderNode;
  searchQuery: string;
  expandedFolders: Set<string>;
  onToggleFolder: (path: string) => void;
  onExpandAll: (folder: FolderNode) => void;
  onCollapseAll: (folder: FolderNode) => void;
}

export function WorkflowFolder({
  folder,
  searchQuery,
  expandedFolders,
  onToggleFolder,
  onExpandAll,
  onCollapseAll,
}: WorkflowFolderProps) {
  const workflowCount =
    folder.workflows.length +
    folder.subfolders.reduce((sum, subfolder) => sum + countWorkflowsInFolder(subfolder), 0);
  const isExpanded = expandedFolders.has(folder.path);

  return (
    <AccordionItem value={folder.path} className="border-b">
      <AccordionTrigger className="hover:no-underline" onClick={() => onToggleFolder(folder.path)}>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Folder
              className={`h-4 w-4 transition-colors ${
                isExpanded ? 'text-blue-600' : 'text-zinc-500'
              }`}
            />
            <span className="font-medium capitalize">{folder.name}</span>
            <span className="text-sm text-zinc-500">({workflowCount} workflows)</span>
          </div>
          <div className="flex items-center gap-1 mr-2">
            <span
              className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onExpandAll(folder);
              }}
              title="Expand all subfolders"
            >
              <ChevronsDown className="h-3 w-3 text-zinc-500" />
            </span>
            <span
              className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onCollapseAll(folder);
              }}
              title="Collapse all subfolders"
            >
              <ChevronsUp className="h-3 w-3 text-zinc-500" />
            </span>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="pl-6 space-y-4">
          {/* Render workflows in this folder first */}
          {folder.workflows.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {folder.workflows.map((workflow, index) => (
                <a
                  key={workflow.id || `${workflow.functionName}-${index}`}
                  href={catalog_show_path(workflow.provider, workflow.functionName)}
                >
                  <AppCard
                    title={workflow.title}
                    description={workflow.description}
                    Icon={Icons[workflow.icon] as Icons.LucideIcon}
                    colorScheme={workflow.colorScheme}
                  />
                </a>
              ))}
            </div>
          )}

          {/* Render subfolders after workflows */}
          {folder.subfolders.length > 0 && (
            <Accordion
              type="multiple"
              value={Array.from(expandedFolders).filter((path) =>
                folder.subfolders.some((sf) => sf.path === path)
              )}
              className="space-y-2"
            >
              {folder.subfolders.map((subfolder) => (
                <WorkflowFolder
                  key={subfolder.path}
                  folder={subfolder}
                  searchQuery={searchQuery}
                  expandedFolders={expandedFolders}
                  onToggleFolder={onToggleFolder}
                  onExpandAll={onExpandAll}
                  onCollapseAll={onCollapseAll}
                />
              ))}
            </Accordion>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
